import { useLingui } from '@lingui/react/macro';
import { Check } from 'lucide-react';
import type { FieldValues } from 'react-hook-form';

import { resolvePatternText } from '@/shared/patterns';
import { cn } from '@/shared/ui/cn';

import type { FlowLeadingStep } from './define-flow';
import { FlowRail } from './flow-rail';
import { useFlowTrail } from './flow-trail';
import type { FlowModel } from './use-flow';

type StepState = 'done' | 'current' | 'pending';

export interface FlowProgressProps<V extends FieldValues> {
  readonly flow: FlowModel<V>;
  /** `vertical`: o trilho do Fluxo guiado (U53), com ícone e o valor escolhido por etapa. */
  readonly orientation?: 'horizontal' | 'vertical';
  /** Etapas anteriores ao fluxo, no trilho vertical (produto e modalidade do Contratar, W02). */
  readonly leading?: readonly FlowLeadingStep[];
  readonly className?: string;
}

function VerticalProgress<V extends FieldValues>({ flow, leading, className }: Omit<FlowProgressProps<V>, 'orientation'>) {
  const steps = useFlowTrail(flow, leading);
  return <FlowRail steps={steps} {...(className ? { className } : {})} />;
}

/**
 * Progresso do fluxo na web: os passos numerados com rótulo (o legado tinha só as bolinhas,
 * PayrollCreditNoteTimeline.tsx:24-72), concluído em verde com ✓, atual marcado, e os alcançáveis
 * como botão para ir a eles.
 */
export function FlowProgress<V extends FieldValues>({ flow, orientation = 'horizontal', leading, className }: FlowProgressProps<V>) {
  if (orientation === 'vertical') return <VerticalProgress flow={flow} {...(leading ? { leading } : {})} {...(className ? { className } : {})} />;
  return <HorizontalProgress flow={flow} {...(className ? { className } : {})} />;
}

function HorizontalProgress<V extends FieldValues>({ flow, className }: { readonly flow: FlowModel<V>; readonly className?: string }) {
  const { i18n, t } = useLingui();
  const stateText: Readonly<Record<StepState, string>> = { done: t`concluído`, current: t`atual`, pending: t`não iniciado` };
  return (
    <nav aria-label={t`Etapas`} data-slot="flow-progress" className={cn('@container/progress rounded-card border border-border bg-card px-4.5 py-3.5', className)}>
      <ol className="flex items-center">
        {flow.steps.map((step, index) => {
          const state: StepState = index < flow.index ? (flow.completed.has(step.id) ? 'done' : 'pending') : index === flow.index ? 'current' : flow.completed.has(step.id) ? 'done' : 'pending';
          const last = index === flow.steps.length - 1;
          const title = resolvePatternText(i18n, step.title);
          // Os anteriores, os seguintes já alcançáveis (concluídos, ou todos no somente leitura).
          const clickable = index !== flow.index && flow.canGoTo(step.id);
          const marker =
            state === 'done' ? (
              <span aria-hidden className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-ok text-primary-foreground">
                <Check className="size-4" />
              </span>
            ) : (
              <span
                aria-hidden
                className={cn(
                  'inline-flex size-7 shrink-0 items-center justify-center rounded-full border-2 font-mono text-xs font-semibold tabular-nums transition-colors duration-(--lume-dur-enter)',
                  state === 'current' ? 'border-primary text-primary' : 'border-input text-muted-foreground',
                )}
              >
                {index + 1}
              </span>
            );
          // Um texto só para o leitor de tela; o rótulo à vista fica fora dele. Container estreito: só o
          // passo atual leva o nome à vista.
          const text = (
            <span className={cn('text-sm font-medium whitespace-nowrap', state === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>
              <span className="sr-only">{t`Passo ${index + 1} de ${flow.steps.length}: ${title}, ${stateText[state]}`}</span>
              <span aria-hidden className={cn(state !== 'current' && 'hidden @min-[721px]/progress:inline')}>
                {title}
              </span>
            </span>
          );
          return (
            <li key={step.id} className={cn('flex min-w-0 items-center', last ? 'flex-none' : 'flex-1')} {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}>
              {clickable ? (
                <button
                  type="button"
                  onClick={() => flow.goTo(step.id)}
                  className="flex shrink-0 items-center gap-2 rounded-control outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {marker}
                  {text}
                </button>
              ) : (
                <span className="flex shrink-0 items-center gap-2">
                  {marker}
                  {text}
                </span>
              )}
              {last ? null : (
                <span aria-hidden className={cn('mx-3 h-0.5 min-w-4 flex-1 rounded-full transition-colors duration-(--lume-dur-enter)', state === 'done' ? 'bg-ok' : 'bg-border')} />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
